import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  ShoppingBag,
  AccessTime,
  Build,
  CheckCircle,
  Cancel,
  Visibility,
  Refresh
} from '@mui/icons-material';
import orderService from '../services/orderService';
import OrderDetailDialog from '../components/OrderDetailDialog';
import AdminPageHeader from '../components/AdminPageHeader';

const STATUS_CONFIG = {
  pendiente: {
    color: 'warning',
    icon: <AccessTime />,
    label: 'Pendiente'
  },
  procesando: {
    color: 'info',
    icon: <Build />,
    label: 'En Proceso'
  },
  aprobado: {
    color: 'success',
    icon: <CheckCircle />,
    label: 'Aprobado'
  },
  rechazado: {
    color: 'error',
    icon: <Cancel />,
    label: 'Rechazado'
  },
  cancelado: {
    color: 'default',
    icon: <Cancel />,
    label: 'Cancelado'
  }
};

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [currentTab, setCurrentTab] = useState('pendiente');
  const [stats, setStats] = useState({
    pendiente: 0,
    procesando: 0,
    aprobado: 0,
    rechazado: 0,
    total: 0
  });

  const loadOrders = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const params = currentTab === 'todos' ? {} : { status: currentTab };
      const result = await orderService.getAll(params);

      if (result.success) {
        setOrders(result.data);
      } else {
        setError(result.message);
      }
    } catch (err) {
      console.error('Error al cargar pedidos:', err);
      setError('Error al cargar los pedidos');
    } finally {
      setLoading(false);
    }
  }, [currentTab]);

  useEffect(() => {
    loadOrders();
    loadStats();
  }, [currentTab, loadOrders]);

  const loadStats = async () => {
    try {
      const result = await orderService.getStats();
      if (result.success) {
        setStats(result.data);
      }
    } catch (err) {
      console.error('Error al cargar estadísticas:', err);
    }
  };

  const handleViewDetails = async (orderId) => {
    try {
      const result = await orderService.getById(orderId);
      if (result.success) {
        setSelectedOrder(result.data);
        setDetailOpen(true);
      } else {
        alert(result.message);
      }
    } catch (err) {
      console.error('Error al cargar detalle:', err);
      alert('Error al cargar el detalle del pedido');
    }
  };

  const handleCloseDetail = () => {
    setDetailOpen(false);
    setSelectedOrder(null);
  };

  const handleOrderUpdated = () => {
    loadOrders();
    loadStats();
    handleCloseDetail();
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS'
    }).format(price || 0);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('es-AR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Container maxWidth={false} disableGutters className="admin-page">
      {/* Header */}
      <AdminPageHeader
        eyebrow="PEDIDOS ONLINE"
        title="Gestión de pedidos"
        subtitle="Administrá y procesá las compras recibidas desde la cava."
        icon={<ShoppingBag />}
        action={<Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={() => {
            loadOrders();
            loadStats();
          }}
        >
          Actualizar
        </Button>}
      />

      {/* Estadísticas */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography variant="caption" color="text.secondary">
                Pendientes
              </Typography>
              <Typography variant="h4" color="warning.main">
                {stats.pendiente || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography variant="caption" color="text.secondary">
                En Proceso
              </Typography>
              <Typography variant="h4" color="info.main">
                {stats.procesando || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography variant="caption" color="text.secondary">
                Aprobados
              </Typography>
              <Typography variant="h4" color="success.main">
                {stats.aprobado || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography variant="caption" color="text.secondary">
                Rechazados
              </Typography>
              <Typography variant="h4" color="error.main">
                {stats.rechazado || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Typography variant="caption" color="text.secondary">
                Total
              </Typography>
              <Typography variant="h4">
                {stats.total || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Tabs de filtros */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Pendientes" value="pendiente" />
          <Tab label="En Proceso" value="procesando" />
          <Tab label="Aprobados" value="aprobado" />
          <Tab label="Rechazados" value="rechazado" />
          <Tab label="Todos" value="todos" />
        </Tabs>
      </Paper>

      {/* Tabla de pedidos */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : orders.length === 0 ? (
        <Card>
          <CardContent>
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <ShoppingBag sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                No hay pedidos {currentTab !== 'todos' ? `en estado "${STATUS_CONFIG[currentTab]?.label}"` : ''}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Los nuevos pedidos aparecerán aquí
              </Typography>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Pedido</strong></TableCell>
                <TableCell><strong>Cliente</strong></TableCell>
                <TableCell><strong>Fecha</strong></TableCell>
                <TableCell><strong>Items</strong></TableCell>
                <TableCell><strong>Total</strong></TableCell>
                <TableCell><strong>Estado</strong></TableCell>
                <TableCell align="right"><strong>Acciones</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orders.map((order) => {
                const statusInfo = STATUS_CONFIG[order.status] || STATUS_CONFIG.pendiente;
                return (
                  <TableRow key={order.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        #{order.orderNumber}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {order.customer?.fullName || 'N/A'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                        {order.customer?.email || order.customer?.username || ''}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(order.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {order.items?.length || 0} producto(s)
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                        {formatPrice(order.total)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={statusInfo.icon}
                        label={statusInfo.label}
                        color={statusInfo.color}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Ver detalles">
                        <IconButton
                          color="primary"
                          size="small"
                          onClick={() => handleViewDetails(order.id)}
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialog de Detalles */}
      {selectedOrder && (
        <OrderDetailDialog
          open={detailOpen}
          onClose={handleCloseDetail}
          order={selectedOrder}
          onOrderUpdated={handleOrderUpdated}
        />
      )}
    </Container>
  );
};

export default Orders;

